import type { AuthContentProps } from './types';
import { BrandSection } from './BrandSection';
import {
  SignUpStep,
  LoginStep,
  VerifyEmailStep,
  VerifyFailedStep,
  ForgotPasswordStep,
  ForgotSuccessStep,
  ResetPasswordStep,
  ResetFailedStep,
} from './steps';
import CloseIcon from '@/assets/icons/close.svg?react';

/**
 * 认证内容组件
 *
 * 包含左侧品牌展示区和右侧认证表单区
 * 右侧内容根据 step 属性切换不同的表单
 *
 * @example
 * ```tsx
 * // 作为弹窗内容使用
 * <Dialog open={open} onOpenChange={setOpen}>
 *   <DialogContent className="p-0 max-w-[1000px] h-[666px]">
 *     <AuthContent
 *       step="signup"
 *       onSignUpSubmit={(email, password, agreed) => { ... }}
 *       onStepChange={setStep}
 *     />
 *   </DialogContent>
 * </Dialog>
 * ```
 */
export function AuthContent({
  step,
  email = '',
  errors,
  loading = false,
  resendCountdown = 0,
  googleAuthUrl,
  onSignUpSubmit,
  onLoginSubmit,
  onForgotPasswordSubmit,
  onResetPasswordSubmit,
  onResendClick,
  onStepChange,
  onPrivacyPolicyClick,
  onForgotPasswordClick,
  showCloseButton = true,
  onClose,
  variant = 'full',
  brandBackgroundMode,
}: AuthContentProps) {
  /**
   * 渲染右侧内容区
   */
  const renderStep = () => {
    const commonProps = {
      errors,
      loading,
      googleAuthUrl,
      onStepChange,
      onPrivacyPolicyClick,
    };

    switch (step) {
      case 'signup':
        return <SignUpStep {...commonProps} onSubmit={onSignUpSubmit} />;

      case 'login':
        return (
          <LoginStep
            {...commonProps}
            onSubmit={onLoginSubmit}
            onForgotPasswordClick={onForgotPasswordClick}
            initialEmail={email}
          />
        );

      case 'verify-email':
        return (
          <VerifyEmailStep
            {...commonProps}
            email={email}
            resendCountdown={resendCountdown}
            onResendClick={onResendClick}
          />
        );

      case 'verify-failed':
        return <VerifyFailedStep {...commonProps} type="verify" />;

      case 'forgot-password':
        return <ForgotPasswordStep {...commonProps} onSubmit={onForgotPasswordSubmit} initialEmail={email} />;

      case 'forgot-success':
        return (
          <ForgotSuccessStep
            {...commonProps}
            email={email}
            resendCountdown={resendCountdown}
            onResendClick={onResendClick}
          />
        );

      case 'reset-password':
        return <ResetPasswordStep {...commonProps} onSubmit={onResetPasswordSubmit} />;

      case 'reset-failed':
        return <ResetFailedStep {...commonProps} type="reset" />;

      default:
        return <SignUpStep {...commonProps} onSubmit={onSignUpSubmit} />;
    }
  };

  const isFormOnly = variant === 'form-only';

  return (
    <div
      className={
        isFormOnly
          ? 'flex min-h-[400px] w-full max-w-[440px] flex-col overflow-hidden  bg-white'
          : 'flex h-[666px] w-[1000px] overflow-hidden rounded-[20px] bg-white'
      }
    >
      {/* 左侧品牌区（form-only 时隐藏） */}
      {!isFormOnly && <BrandSection backgroundMode={brandBackgroundMode} />}

      {/* 右侧内容区 */}
      <div
        className={
          isFormOnly
            ? 'relative flex min-h-[400px] w-full flex-1 flex-col items-center justify-center px-6 py-8'
            : 'relative flex h-full w-[500px] flex-col items-center justify-center px-[60px]'
        }
      >
        {showCloseButton && (
          <button
            onClick={onClose}
            className="absolute right-[20px] top-[20px] flex h-[32px] w-[32px] cursor-pointer items-center justify-center rounded-[4px] p-[4px] transition-colors hover:bg-[#f5f5f5]"
            aria-label="Close"
          >
            <CloseIcon className="h-[24px] w-[24px]" />
          </button>
        )}
        <div className="w-full max-w-[380px]">{renderStep()}</div>
      </div>
    </div>
  );
}

// 导出类型
export type { AuthContentProps, AuthStep } from './types';
export type {
  FormErrors,
  PasswordValidation,
  StepProps,
  SignUpStepProps,
  LoginStepProps,
  VerifyEmailStepProps,
  FailedStepProps,
  ForgotPasswordStepProps,
  ForgotSuccessStepProps,
  ResetPasswordStepProps,
} from './types';

// 导出 hooks
export { useCountdown } from './hooks/useCountdown';
export { usePasswordValidation, isPasswordValid } from './hooks/usePasswordValidation';
export { useSignUpModal } from './SignUpModalExample';
export type { SignUpModalExampleProps } from './SignUpModalExample';

// 导出示例组件
export { AuthContentExample } from './AuthContentExample';

// 导出子组件（方便单独使用）
export { BrandSection } from './BrandSection';
export * from './steps';
export * from './shared';

export default AuthContent;
